(ns app.address.parser
  (:require #?(:clj [app.i18n.model :as i18n-model])
            #?(:clj [clojure.tools.logging :as log])
            #?(:clj [core.config :as config])
            #?(:clj [core.i18n.core :as i18n])
            [app.domain.address.model :as address-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.util.util :as util]
            [potpuri.core :as p]
            [schema.core :as s]))

(defn log-error [msg val]
  #?(:clj (log/error msg val)
     :cljs (.error js/console msg val)))

(defn trim-and-upper-case [text]
  (when (and (string? text)
             (-> text str/blank? not))
    (-> (str/trim text) str/upper-case)))




(def additional-abbreviations {"LN" :street-type/lane
                               "WY" :street-type/way})

(def additional-street-directions {"EAST"      :street-direction/E
                                   "NORTH"     :street-direction/N
                                   "NORTHEAST" :street-direction/NE
                                   "NORTHWEST" :street-direction/NW
                                   "SOUTH"     :street-direction/S
                                   "SOUTHEAST" :street-direction/SE
                                   "SOUTHWEST" :street-direction/SW
                                   "WEST"      :street-direction/W})

(defmulti applicable-street-types (fn [language] language))

(defmethod applicable-street-types :default [_]
  (set (concat address-model/street-types
               (map second additional-abbreviations))))

(defmethod applicable-street-types :fi [_]
  #{:street-type/park
    :street-type/parkway
    :street-type/way
    :street-type/road
    :street-type/trail
    :street-type/beach
    :street-type/boulevard
    :street-type/alley
    :street-type/highway
    :street-type/street})

(defn street-type-abbreviation->street-type [language tr]
  (let [street-types (applicable-street-types language)]
    (-> (reduce (fn [acc k]
                  (assoc acc (tr [(keyword "street-type-abbreviation" (name k))]) k))
                {}
                (set/intersection (set address-model/street-types) street-types))
        (merge (select-keys additional-abbreviations street-types)))))


(defn street-type-full->street-type [language tr]
  (let [street-types (applicable-street-types language)]
    (reduce (fn [acc k]
              (assoc acc (str/upper-case (tr [k])) k))
            {}
            (set/intersection (set address-model/street-types) street-types))))


(defn street-direction-str->street-direction [tr]
  (merge (reduce (fn [acc k]
                   (assoc acc (tr [k]) k))
                 {}
                 address-model/street-directions)
         additional-street-directions))


(defn unit-abbreviation->unit-designator [tr]
  (reduce (fn [acc k]
            (assoc acc (tr [(keyword "unit-designator-abbreviation" (name k))]) k))
          {}
          address-model/unit-designators))


(defn parse-street-type [{:keys [street-type-abbreviation->street-type
                                 street-type-full->street-type]}
                         street-type]
  (when-let [street-type (trim-and-upper-case street-type)]
    (or (street-type-abbreviation->street-type street-type)
        (street-type-full->street-type street-type)
        (log-error "Could not parse street type:" street-type))))


(defn parse-street-direction [{:keys [street-direction-str->street-direction]}
                              street-direction]
  (when-let [street-direction (trim-and-upper-case street-direction)]
    (when-not (str/blank? street-direction)
      (or (street-direction-str->street-direction street-direction)
          (log-error "Could not parse street direction:" street-direction)))))


(defn parse-street-type-from-name [{:keys [street-type-full->street-type] :as ctx} street-name]
  (when-not (str/blank? street-name)
    (->> (keys street-type-full->street-type)
         (filter #(str/includes? street-name %))
         first
         (parse-street-type ctx))))

(defn street-type-abbreviation-patterns [street-type-abbreviation->street-type]
  (->> street-type-abbreviation->street-type
       keys
       (map (juxt identity #(re-pattern (str "^" % "\\s+|\\s+" % "\\s+"))))))

(defn parse-street-type-abbreviation-from-name [{:keys [street-type-abbreviation-patterns] :as ctx} street-name]
  (when-not (str/blank? street-name)
    (->> street-type-abbreviation-patterns
         (filter #(re-find (second %) street-name))
         first
         first
         (parse-street-type ctx))))


(defn parse-unit-designator [{:keys [unit-abbreviation->unit-designator]} unit-designator]
  (when-let [unit-designator (trim-and-upper-case unit-designator)]
    (when-not (str/blank? unit-designator)
      (or (unit-abbreviation->unit-designator unit-designator)
          (log-error "Could not parse unit designator:" unit-designator)))))

(defn wrap-parens [s]
  (str "(" s ")"))

(defn direction-pattern [street-direction-str->street-direction]
  (->> (keys street-direction-str->street-direction)
       (str/join "|")
       wrap-parens))


(defn street-type-pattern-str [street-type-full->street-type
                               street-type-abbreviation->street-type]
  (-> (->> (concat (keys street-type-full->street-type)
                   (keys street-type-abbreviation->street-type))
           (str/join "|")
           wrap-parens)
      (str "\\.?")))


(defn unit-designator-pattern [unit-abbreviation->unit-designator]
  (->> (keys unit-abbreviation->unit-designator)
       (str/join "|")
       wrap-parens))

(def street-number-or-concatenation-pattern-str
  "\\d+(?:\\s?[,-/]\\s?\\d+)?")

(def street-number-suffix-pattern-str
  "[A-Z]{0,1}")

(def street-number-pattern
  (re-pattern
    (str "(" street-number-or-concatenation-pattern-str ")(" street-number-suffix-pattern-str ")")))

(def street-number-group-pattern-str
  (str "("
       street-number-or-concatenation-pattern-str
       street-number-suffix-pattern-str
       ")"))

(def space-pattern-str "\\s+")

(def address-text-pattern-str "([^\\s]+(?:\\s[^\\s]+)*)")

(def base-address-pattern-str
  (str "^" street-number-group-pattern-str space-pattern-str address-text-pattern-str))


(defn unit-pattern-str [unit-designator-pattern]
  (str "(?: \\(?\\s?" unit-designator-pattern "\\s?([A-Za-z0-9]+)?\\s?\\)?)?$"))


(defmulti address-pattern (fn [lang _ _] lang))

(defmethod address-pattern :default [_ street-type-pattern-str unit-pattern-str]
  (re-pattern (str base-address-pattern-str space-pattern-str street-type-pattern-str unit-pattern-str)))

(defmethod address-pattern :fr [_ street-type-pattern-str unit-pattern-str]
  (re-pattern (str "^"
                   street-number-group-pattern-str
                   space-pattern-str
                   street-type-pattern-str
                   space-pattern-str
                   address-text-pattern-str
                   unit-pattern-str)))

(defmethod address-pattern :fi [_ _ _] ;; NOTE the simple finnish address regexp does not use params
  (re-pattern (str "^"
                   address-text-pattern-str
                   space-pattern-str
                   street-number-group-pattern-str)))

(defmulti address-with-direction-pattern (fn [lang _ _ _] lang))

(defmethod address-with-direction-pattern :default
  [_ direction-pattern street-type-pattern-str unit-pattern-str]
  (re-pattern (str base-address-pattern-str
                   space-pattern-str
                   street-type-pattern-str
                   space-pattern-str
                   direction-pattern
                   unit-pattern-str)))

(defmethod address-with-direction-pattern :fr
  [_ direction-pattern street-type-pattern-str unit-pattern-str]
  (re-pattern (str "^"
                   street-number-group-pattern-str
                   space-pattern-str
                   street-type-pattern-str
                   space-pattern-str
                   address-text-pattern-str
                   space-pattern-str
                   direction-pattern
                   unit-pattern-str)))


(def fallback-address-pattern
  (re-pattern (str street-number-group-pattern-str " (.+)$")))


(defn unit-pattern [unit-pattern-str]
  (re-pattern unit-pattern-str))


(defn parse-unit-designator-and-number [{:keys [unit-pattern]
                                         :as   ctx}
                                        full-address]
  (let [[_ unit-designator unit-number] (re-find unit-pattern full-address)]
    (when unit-designator
      (p/assoc-if {:address/unit-designator (parse-unit-designator ctx unit-designator)}
                  :address/unit-number unit-number))))

(defmulti address-input-components (fn [{:keys [language]} _] language))

(defmethod address-input-components :default
  [{:keys [address-pattern
           address-with-direction-pattern]}
   full-address]
  (let [[_
         house-num
         street
         street-type
         direction]  (or (re-find address-with-direction-pattern
                                  full-address)
                         (some->> (re-find address-pattern full-address)
                                  (take 4))
                         (some->> (re-find fallback-address-pattern full-address)
                                  (take 3)))]
    (util/mmap house-num street street-type direction)))

(defmethod address-input-components :fr
  [{:keys [address-pattern
           address-with-direction-pattern]}
   full-address]
  (let [[_
         house-num
         street-type
         street
         direction]  (or (re-find address-with-direction-pattern
                                  full-address)
                         (some->> (re-find address-pattern full-address)
                                  (take 4))
                         (some->> (re-find fallback-address-pattern full-address)
                                  (take 3)))]
    (util/mmap house-num street street-type direction)))

(defmethod address-input-components :fi
  [{:keys [address-pattern]}
   full-address]
  (let [[_
         street
         house-num] (re-find address-pattern full-address)]
    (util/mmap {:street-type nil} house-num street)))


(defn build-structured-address [ctx house-num-and-suffix street street-type direction full-address]
  (let [house-num-and-suffix (str house-num-and-suffix)
        [_ house-num suffix] (re-find street-number-pattern house-num-and-suffix)]
    (->> {:address/street-number        (or (when-not (str/blank? house-num)
                                              house-num)
                                            (when-not (str/blank? house-num-and-suffix)
                                              house-num-and-suffix))
          :address/street-number-suffix (when-not (str/blank? suffix) suffix)
          :address/street-name          street
          :address/street-type          (or (parse-street-type ctx street-type)
                                            (parse-street-type-from-name ctx street)
                                            (parse-street-type-abbreviation-from-name ctx street))
          :address/street-direction     (parse-street-direction ctx direction)
          :address/full-address         full-address}
         (merge
           (parse-unit-designator-and-number ctx full-address))
         (p/remove-vals nil?))))

(s/defschema ParserOpts
  {:tr          (s/pred fn?)
   :country     s/Str
   :subdivision (s/maybe s/Keyword)
   :language    s/Keyword})

(s/defn parser-ctx [{:keys [tr language]} :- ParserOpts]
  (let [street-direction-str->street-direction (street-direction-str->street-direction tr)
        street-type-full->street-type          (street-type-full->street-type language tr)
        street-type-abbreviation->street-type  (street-type-abbreviation->street-type language tr)
        unit-abbreviation->unit-designator     (unit-abbreviation->unit-designator tr)
        street-type-pattern-str                (street-type-pattern-str street-type-full->street-type
                                                                        street-type-abbreviation->street-type)
        unit-designator-pattern                (unit-designator-pattern unit-abbreviation->unit-designator)
        unit-pattern-str                       (unit-pattern-str unit-designator-pattern)
        direction-pattern                      (direction-pattern street-direction-str->street-direction)]
    (util/mmap {:street-type-abbreviation-patterns (street-type-abbreviation-patterns street-type-abbreviation->street-type)
                :address-pattern                   (address-pattern language
                                                                    street-type-pattern-str
                                                                    unit-pattern-str)
                :address-with-direction-pattern    (address-with-direction-pattern language
                                                                                   direction-pattern
                                                                                   street-type-pattern-str
                                                                                   unit-pattern-str)
                :unit-pattern                      (unit-pattern unit-pattern-str)}
               unit-designator-pattern
               street-direction-str->street-direction
               street-type-full->street-type
               street-type-abbreviation->street-type
               unit-abbreviation->unit-designator
               direction-pattern
               language)))

(defn parser-fn [params]
  (let [ctx (parser-ctx params)]
    (fn [full-address]
      (if-let [full-address (trim-and-upper-case full-address)]
        (let [{:keys [house-num
                      street
                      street-type
                      direction]} (address-input-components ctx full-address)]
          (build-structured-address ctx house-num street street-type direction full-address))
        {}))))


#?(:clj
   (let [parser (parser-fn {:tr          i18n/trd
                            :language    i18n-model/default-lang
                            :subdivision :CA-ON ;; TODO
                            :country     config/country})]
     (defn parse-components-from-full-address
       [full-address]
       (parser full-address))))
